import React, { useContext, useEffect, useState } from "react";
import produitDataFetcher from "dataProvider/Produit";
import Loader from "ui/Loader";
import BackToId from "ui/Button/BackToId";
import { IntlContext, ListContext } from "context";
import { Link } from "react-router-dom";
import Warnings from "../Common/Warnings";
import { matomoPush } from "../../Produit/helpers";

const ListStats: React.FC<{ activeFilters: any }> = ({ activeFilters }) => {
    const intl = useContext(IntlContext);
    const [downloading, setDownloading] = useState<boolean>(false);
    const { changeFetcherParams, data, isLoading, activeFetch } = useContext(ListContext);
    const [hasTypeMarque, setHasTypeMarque] = useState<boolean>(false);

    useEffect(() => {
        setHasTypeMarque(!!(data?.active_filters?.filter_type_marque || []).length);
    }, [data]);

    if (isLoading || !data) {
        return (
            <div className="container container--table">
                <Loader message={intl.formatMessage("generic.loader.loading.label")} />
            </div>
        );
    }

    const exportProduits = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            event.preventDefault();
            setDownloading(true);
            matomoPush([["trackEvent", "Export outil de visualisation", "Statistiques descriptives", JSON.stringify(activeFilters?.matomo), 1]]);
            produitDataFetcher
                .statsExport(activeFilters)
                .then((data: any) => {
                    let file = window.URL.createObjectURL(data.content);
                    let tempLink = document.createElement("a");
                    tempLink.href = file;
                    tempLink.download = data.name;
                    tempLink.click();
                    tempLink.parentNode?.removeChild(tempLink);
                    window.URL.revokeObjectURL(file);
                })
                .catch((err) => {
                    console.error(err);
                })
                .finally(() => setDownloading(false));
        } catch (e) {
            console.error(e);
        }
    };

    const totalResults = (): string => {
        const total = data?.pager?.total;
        if (typeof total !== "number") {
            return "";
        }
        if (total === 0) {
            return intl.formatMessage("produit.label.no_result");
        }
        return (
            `${total} ` +
            (total > 1 ? intl.formatMessage("produit.label.results") : intl.formatMessage("produit.label.result"))
        );
    };

    const goToPage = (page: number) => {
        changeFetcherParams(
            Object.assign({}, activeFilters, {
                page,
                order: data.pager.order,
                direction: data.pager.direction,
                per_page: data.pager.per_page,
            })
        );
    };

    const paginationList = () => {
        const pages = [];

        if (data?.pager?.last_page === 1) {
            return [];
        }

        if (data?.pager?.last_page > 1 && data?.pager?.current_page > 1) {
            pages.push(
                <li key="pagination-first" className="pagination__item">
                    <Link
                        className="pagination__item--link"
                        to="#"
                        onClick={() => {
                            goToPage(1);
                        }}
                    >
                        <i className="icon-angle-double-left-solid"></i>
                    </Link>
                </li>
            );

            pages.push(
                <li key="pagination-prev" className="pagination__item">
                    <Link
                        className="pagination__item--link"
                        to="#"
                        onClick={() => {
                            goToPage(data?.pager?.current_page - 1);
                        }}
                    >
                        <i className="icon-arrow-left"></i>
                    </Link>
                </li>
            );
        }

        for (let i = 1; i <= data?.pager?.last_page; i++) {
            const minMax = 4;
            const pageMin = Math.max(
                1,
                Math.min(data?.pager?.current_page - minMax, data?.pager?.last_page - 2 * minMax)
            );
            const pageMax = Math.min(data?.pager?.last_page, pageMin + 2 * minMax);
            if (i >= pageMin && i <= pageMax) {
                pages.push(
                    <li
                        key={i}
                        className={`pagination__item ${
                            data?.pager?.current_page === i ? "pagination__item--active" : ""
                        }`}
                    >
                        <Link
                            className="pagination__item--link"
                            to="#"
                            onClick={() => {
                                goToPage(i);
                            }}
                        >
                            {i}
                        </Link>
                    </li>
                );
            }
        }

        if (data?.pager?.last_page > 1 && data?.pager?.current_page < data?.pager?.last_page) {
            pages.push(
                <li key="pagination-next" className="pagination__item">
                    <Link
                        className="pagination__item--link"
                        to="#"
                        onClick={() => {
                            goToPage(data?.pager?.current_page + 1);
                        }}
                    >
                        <i className="icon-arrow-right"></i>
                    </Link>
                </li>
            );

            pages.push(
                <li key="pagination-last" className="pagination__item">
                    <Link
                        className="pagination__item--link"
                        to="#"
                        onClick={() => {
                            goToPage(data?.pager?.last_page);
                        }}
                    >
                        <i className="icon-angle-double-right-solid"></i>
                    </Link>
                </li>
            );
        }
        return pages;
    };

    const exportButton = () => {
        return <div className="container--export">
        <button
            type="button"
            className="cta cta--secondary export"
            title={intl.formatMessage("generic.export")}
            disabled={downloading}
            onClick={exportProduits}
        >
            {downloading
                ? intl.formatMessage("generic.loader.loading.label")
                : intl.formatMessage("generic.export")}
        </button>
    </div>
    };

    if (!data || data.length <= 0) {
        return (
            <div className="container container--table">
                <div className="flex margin-bottom--4">
                    <h1>{intl.formatMessage("produit.stats.descriptives.title")}</h1>
                </div>
                <div className="stats-container-top">
                    <span className="results">{totalResults()}</span>
                </div>
                <Warnings warnings={data?.warnings} />
            </div>
        );
    }

    matomoPush([
        ["setReferrerUrl", window.location.href],
        ["setCustomUrl", `${window.location.href}/descriptives`],
    ]);

    return (
        <div className="container container--table">
            <div className="flex margin-bottom--4">
                <h1>{intl.formatMessage("produit.stats.descriptives.title")}</h1>
            </div>

            {exportButton()}

            <Warnings warnings={data?.warnings} />

            {activeFetch && (
                <React.Fragment>
                    <div className="stats-container-top">
                        <span className="results">{totalResults()}</span>
                    </div>

                    <table className="search-table">
                        <thead className="row-group">
                            <tr className="header-row">
                                <th className="header-cell">
                                    {intl.formatMessage("produit.stats.table.header.secteur")}
                                </th>
                                <th className="header-cell">
                                    {intl.formatMessage("produit.stats.table.header.famille")}
                                </th>
                                <th className="header-cell">
                                    {intl.formatMessage("produit.stats.table.header.annee_collecte")}
                                </th>
                                <th className="header-cell">
                                    {intl.formatMessage("produit.stats.table.header.nutriment")}
                                </th>
                                {hasTypeMarque && (
                                    <th className="header-cell">
                                        {intl.formatMessage("produit.stats.table.header.type_marque")}
                                    </th>
                                )}
                                <th className="header-cell">
                                    {intl.formatMessage("produit.stats.table.header.observations")}
                                </th>
                                <th className="header-cell">{intl.formatMessage("produit.stats.table.header.min")}</th>
                                <th className="header-cell">{intl.formatMessage("produit.stats.table.header.max")}</th>
                                <th className="header-cell">
                                    {intl.formatMessage("produit.stats.table.header.quartile1")}
                                </th>
                                <th className="header-cell">
                                    {intl.formatMessage("produit.stats.table.header.mediane")}
                                </th>
                                <th className="header-cell">
                                    {intl.formatMessage("produit.stats.table.header.quartile3")}
                                </th>
                                <th className="header-cell">
                                    {intl.formatMessage("produit.stats.table.header.moyenne")}
                                </th>
                                <th className="header-cell">
                                    {intl.formatMessage("produit.stats.table.header.ecart_type")}
                                </th>
                                <th className="header-cell">
                                    {intl.formatMessage("produit.stats.table.header.unite")}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data || []).map((row) => {
                                return row.nutriments.map((nutriment: any, key: number) => {
                                    let [, decimalPart] = nutriment.quartiles.min.toString().split(".");
                                    const minPartDec = decimalPart?.length || 0;
                                    [, decimalPart] = nutriment.quartiles.max.toString().split(".");
                                    const maxPartDec = decimalPart?.length || 0;
                                    return (
                                        <tr key={key}>
                                            <td className="cell">{row.secteur}</td>
                                            <td className="cell">{row.famille}</td>
                                            <td className="cell">{row.annee_collecte}</td>
                                            <td className="cell">{nutriment.nutriment}</td>
                                            {hasTypeMarque && <td className="cell">{row.type_marque}</td>}
                                            <td className="cell">{nutriment.observations}</td>
                                            <td className="cell">
                                                {intl.formatNumber(nutriment.quartiles.min, {
                                                    maximumFractionDigits: minPartDec,
                                                    minimumFractionDigits: 0,
                                                })}
                                            </td>
                                            <td className="cell">
                                                {intl.formatNumber(nutriment.quartiles.max, {
                                                    maximumFractionDigits: maxPartDec,
                                                    minimumFractionDigits: 0,
                                                })}
                                            </td>
                                            <td className="cell">
                                                {intl.formatNumber(nutriment.quartiles.quartile1, {
                                                    maximumFractionDigits: 2,
                                                    minimumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td className="cell">
                                                {intl.formatNumber(nutriment.quartiles.median, {
                                                    maximumFractionDigits: 2,
                                                    minimumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td className="cell">
                                                {intl.formatNumber(nutriment.quartiles.quartile3, {
                                                    maximumFractionDigits: 2,
                                                    minimumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td className="cell">
                                                {intl.formatNumber(nutriment.quartiles.average, {
                                                    maximumFractionDigits: 2,
                                                    minimumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td className="cell">
                                                {intl.formatNumber(nutriment.quartiles.ecart_type, {
                                                    maximumFractionDigits: 2,
                                                    minimumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td className="cell">
                                                {nutriment.unite}/100{nutriment.type_unite}
                                            </td>
                                        </tr>
                                    );
                                }, row);
                            })}
                        </tbody>
                    </table>

                    {/* PAGINATION */}
                    <nav className="pagination">
                        <ul className="pagination__list">{paginationList()}</ul>
                    </nav>

                    {exportButton()}

                    <BackToId label={intl.formatMessage("generic.go_to.top")} toId="pageup"/>
                </React.Fragment>
            )}
        </div>
    );
};

export default ListStats;
